import React, { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Bounce, toast } from "react-toastify"
import CustomButton from "../../components/OnboardingComponents/CustomButton"
import PageHeader from "../../components/DashboardComponents/PageHeader"
import Pagination from "../../components/Listing/Pagination"
import EmptyState from "../../components/Common/EmptyState"
import TruncatedText from "../../components/Common/TruncatedText"
import {
  AddCollaboratorModal,
  XpLevelGuideModal,
} from "../../components/Modals"
import { ICONS } from "../../constants/icons"
import { get, post } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"
import {
  AVAILABLE_REWARDS_WITH,
  EMPTY_REWARD_DASHBOARD,
  REWARD_DASHBOARD_TYPES,
  XP_ACTIVITY_WITH,
  extractPaginatedList,
  formatLevelOrdinal,
  mapAvailableReward,
  mapRewardDashboardStats,
  mapXpActivity,
} from "./rewardsData"
import { getXpProgress } from "../../helpers/xpLevel"

const PAGE_SIZE = 10
const ACTIVITY_SKELETON_COUNT = 6
const REWARD_SKELETON_COUNT = 4

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const STAT_CARDS = [
  {
    key: "xp",
    label: "Total XP Earned",
    unit: "XP",
    icon: ICONS.rewardsMedal,
    tone: "xp",
  },
  {
    key: "level",
    label: "Current Level",
    unit: "Level",
    icon: ICONS.rewardsLevelUp,
    tone: "level",
  },
  {
    key: "rewards",
    label: "Treasure Claimed",
    unit: "Total Treasure",
    icon: ICONS.rewardsXpRed,
    tone: "rewards",
  },
  {
    key: "invites",
    label: "Total Invites Earned",
    unit: "Invites",
    icon: ICONS.rewardsSendSquare,
    tone: "invites",
  },
]

const RewardsXp = () => {
  const navigate = useNavigate()

  const [currentPage, setCurrentPage] = useState(1)
  const [activities, setActivities] = useState([])
  const [activityTotal, setActivityTotal] = useState(0)
  const [activityPageSize, setActivityPageSize] = useState(PAGE_SIZE)
  const [isActivityLoading, setIsActivityLoading] = useState(true)
  const [availableRewards, setAvailableRewards] = useState([])
  const [isRewardsLoading, setIsRewardsLoading] = useState(true)
  const [claimingId, setClaimingId] = useState(null)
  const [isInviteOpen, setIsInviteOpen] = useState(false)
  const [isGuideOpen, setIsGuideOpen] = useState(false)
  const [dashboardStats, setDashboardStats] = useState(EMPTY_REWARD_DASHBOARD)
  const [isStatsLoading, setIsStatsLoading] = useState(true)

  const { xpValue, xpMax } = getXpProgress(dashboardStats.totalXp)
  const xpLevel = dashboardStats.currentLevel || 1
  const progressPercent = dashboardStats.progressPercent
  const progressLabel =
    dashboardStats.progressLabel ||
    `${Math.max(0, xpMax - xpValue).toLocaleString()} XP to Level ${xpLevel + 1}`
  const levelOrdinal = formatLevelOrdinal(xpLevel)

  const statValues = {
    xp: dashboardStats.totalXp.toLocaleString(),
    level: xpLevel,
    rewards: dashboardStats.rewardsClaimed.toLocaleString(),
    invites: dashboardStats.totalInvites.toLocaleString(),
  }

  const fetchDashboardStats = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setIsStatsLoading(true)
      try {
        const response = await get(endpoints.getDashboardStats, {
          params: { "types[]": REWARD_DASHBOARD_TYPES },
        })

        if (response?.data?.success) {
          const mapped = mapRewardDashboardStats(response.data.result || {})
          setDashboardStats(mapped)
        } else if (response) {
          toastApiError(response, "Failed to load reward stats")
          if (!silent) setDashboardStats(EMPTY_REWARD_DASHBOARD)
        }
      } catch (err) {
        toastApiError(err, "Failed to load reward stats")
        if (!silent) setDashboardStats(EMPTY_REWARD_DASHBOARD)
      } finally {
        if (!silent) setIsStatsLoading(false)
      }
    },
    [],
  )

  const fetchActivities = useCallback(async () => {
    setIsActivityLoading(true)
    try {
      const response = await get(endpoints.getXpActivity, {
        params: {
          page: currentPage,
          per_page: PAGE_SIZE,
          sort_by: "created_at",
          sort_direction: "desc",
          "with[]": XP_ACTIVITY_WITH,
        },
      })

      if (response?.data?.success) {
        const page = extractPaginatedList(response)
        setActivities(page.list.map(mapXpActivity))
        setActivityTotal(page.total)
        setActivityPageSize(page.perPage)

        const lastPage = Number(page.lastPage)
        const totalPages = Math.max(
          1,
          Number.isFinite(lastPage) && lastPage > 0
            ? lastPage
            : Math.ceil((page.total || 0) / (page.perPage || PAGE_SIZE)) || 1,
        )
        if (currentPage > totalPages) setCurrentPage(totalPages)
      } else if (response) {
        toastApiError(response, "Failed to load XP activity")
        setActivities([])
        setActivityTotal(0)
      }
    } catch (err) {
      toastApiError(err, "Failed to load XP activity")
      setActivities([])
      setActivityTotal(0)
    } finally {
      setIsActivityLoading(false)
    }
  }, [currentPage])

  const fetchAvailableRewards = useCallback(async () => {
    setIsRewardsLoading(true)
    try {
      const response = await get(endpoints.getAvailableRewards, {
        params: {
          sort_direction: "asc",
          "with[]": AVAILABLE_REWARDS_WITH,
        },
      })

      if (response?.data?.success) {
        const page = extractPaginatedList(response)
        setAvailableRewards(prev => {
          const claimedIds = new Set(
            prev
              .filter(item => item.claimed)
              .map(item => item.claimId || item.id)
              .filter(Boolean),
          )
          return page.list
            .map(mapAvailableReward)
            .filter(item => item.id)
            .map(item => ({
              ...item,
              claimed:
                item.claimed ||
                claimedIds.has(item.claimId) ||
                claimedIds.has(item.id),
            }))
        })
      } else if (response) {
        toastApiError(response, "Failed to load available rewards")
        setAvailableRewards([])
      }
    } catch (err) {
      toastApiError(err, "Failed to load available rewards")
      setAvailableRewards([])
    } finally {
      setIsRewardsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchActivities()
  }, [fetchActivities])

  useEffect(() => {
    fetchAvailableRewards()
  }, [fetchAvailableRewards])

  useEffect(() => {
    fetchDashboardStats()
  }, [fetchDashboardStats])

  const hasClaimedAtCurrentLevel = Boolean(
    dashboardStats.hasClaimedAtCurrentLevel,
  )

  const handleClaimReward = async reward => {
    if (
      !reward?.claimId ||
      reward.claimed ||
      hasClaimedAtCurrentLevel ||
      claimingId
    ) {
      return
    }

    setClaimingId(reward.claimId)
    try {
      const response = await post(endpoints.claimReward(reward.claimId), {})
      if (!response?.data?.success) {
        toastApiError(response, "Failed to claim reward")
        return
      }

      toast.success(response.data.message || "Reward claimed", TOAST_OPTIONS)
      setAvailableRewards(prev =>
        prev.map(item =>
          item.id === reward.id || item.claimId === reward.claimId
            ? { ...item, claimed: true }
            : item,
        ),
      )
      setDashboardStats(prev => ({
        ...prev,
        hasClaimedAtCurrentLevel: true,
        rewardsClaimed: (Number(prev.rewardsClaimed) || 0) + 1,
      }))
      fetchAvailableRewards()
      fetchDashboardStats({ silent: true })
    } catch (err) {
      toastApiError(err, "Failed to claim reward")
    } finally {
      setClaimingId(null)
    }
  }

  return (
    <div className="page-content">
      <div className="rewards">
        <PageHeader
          className="rewards__header"
          title="XP, Level & Treasure"
          subtitle="Track your progress, earn XP, level up, and claim gifts donated by your community to help you along your journey."
          actions={
            <>
              <CustomButton
                variant="secondary"
                size="md"
                type="button"
                onClick={() => setIsGuideOpen(true)}
              >
                XP &amp; Level Guide
              </CustomButton>
              <CustomButton
                variant="primary"
                size="md"
                type="button"
                trailingIconSrc={ICONS.plus}
                trailingIconAlt=""
                onClick={() => setIsInviteOpen(true)}
              >
                Invite Collaborator
              </CustomButton>
            </>
          }
        />

        <div className="rewards__stats">
          {STAT_CARDS.map(card => (
            <div
              key={card.key}
              className={`rewards__stat-card rewards__stat-card--${card.tone}`}
            >
              <div className="rewards__stat-top">
                <span className="rewards__stat-label">{card.label}</span>
                <img
                  src={card.icon}
                  alt=""
                  className="rewards__stat-icon"
                  width={16}
                  height={16}
                />
              </div>
              <div className="rewards__stat-bottom">
                {isStatsLoading ? (
                  <span className="amok-skeleton rewards__skel-line rewards__skel-line--stat" />
                ) : (
                  <span className="rewards__stat-value">
                    {statValues[card.key]}
                  </span>
                )}
                <span className="rewards__stat-unit">{card.unit}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="rewards__layout">
          <div className="rewards__main">
            <div className="rewards__panel rewards__panel--activity">
              <div className="rewards__panel-header">
                <h2 className="rewards__panel-title">Recent XP Activity</h2>
                <CustomButton
                  variant="primary"
                  size="sm"
                  type="button"
                  className="rewards__view-all"
                  trailingIconSrc={ICONS.arrowRight}
                  trailingIconAlt=""
                  onClick={() => navigate("/rewards/activity")}
                >
                  View All
                </CustomButton>
              </div>

              <div className="projects-listing__table-wrapper rewards__table-wrap">
                <div
                  className="projects-listing__table rewards__table"
                  role="table"
                  aria-label="Recent XP Activity"
                >
                  <div
                    className="projects-listing__table-row projects-listing__table-row--head"
                    role="row"
                  >
                    <div role="columnheader">Activity</div>
                    <div role="columnheader">Date</div>
                    <div role="columnheader">Time</div>
                    <div role="columnheader">Earned XP</div>
                  </div>

                  {isActivityLoading
                    ? Array.from({ length: ACTIVITY_SKELETON_COUNT }).map(
                        (_, index) => (
                          <div
                            key={`xp-activity-skel-${index}`}
                            className="projects-listing__table-row"
                            role="row"
                            aria-hidden="true"
                          >
                            <div
                              className="projects-listing__table-cell projects-listing__table-cell--title"
                              role="cell"
                            >
                              <span className="projects-listing__table-title">
                                <span className="rewards__activity-icon">
                                  <span className="amok-skeleton" />
                                </span>
                                <span className="amok-skeleton rewards__skel-line rewards__skel-line--activity" />
                              </span>
                            </div>
                            <div
                              className="projects-listing__table-cell"
                              role="cell"
                            >
                              <span className="amok-skeleton rewards__skel-line" />
                            </div>
                            <div
                              className="projects-listing__table-cell"
                              role="cell"
                            >
                              <span className="amok-skeleton rewards__skel-line rewards__skel-line--sm" />
                            </div>
                            <div
                              className="projects-listing__table-cell rewards__earned"
                              role="cell"
                            >
                              <span className="amok-skeleton rewards__skel-line rewards__skel-line--sm" />
                            </div>
                          </div>
                        ),
                      )
                    : null}

                  {!isActivityLoading
                    ? activities.map(row => (
                        <div
                          key={row.id}
                          className="projects-listing__table-row"
                          role="row"
                        >
                          <div
                            className="projects-listing__table-cell projects-listing__table-cell--title"
                            role="cell"
                          >
                            <span className="projects-listing__table-title">
                              <span
                                className="rewards__activity-icon"
                                aria-hidden="true"
                              >
                                <img
                                  src={ICONS.rewardsActivity}
                                  alt=""
                                  width={16}
                                  height={16}
                                />
                              </span>
                              <TruncatedText
                                className="rewards__activity-name"
                                title={row.activity}
                              >
                                {row.activity}
                              </TruncatedText>
                            </span>
                          </div>
                          <div
                            className="projects-listing__table-cell"
                            role="cell"
                          >
                            <TruncatedText>{row.date}</TruncatedText>
                          </div>
                          <div
                            className="projects-listing__table-cell"
                            role="cell"
                          >
                            <TruncatedText>{row.time}</TruncatedText>
                          </div>
                          <div
                            className="projects-listing__table-cell rewards__earned"
                            role="cell"
                          >
                            +{row.earned}xp
                          </div>
                        </div>
                      ))
                    : null}
                </div>

                {!isActivityLoading && activities.length === 0 ? (
                  <EmptyState
                    size="sm"
                    title="No XP activity yet"
                    description="Earn XP by creating projects and collaborating."
                  />
                ) : null}
              </div>

              <Pagination
                className="rewards__pagination"
                currentPage={currentPage}
                totalItems={activityTotal}
                pageSize={activityPageSize}
                onPageChange={setCurrentPage}
                itemLabel="Activities"
                prevIconSrc={ICONS.chevronLeft}
                nextIconSrc={ICONS.chevronRight}
              />
            </div>
          </div>

          <aside className="rewards__aside">
            <div className="rewards__panel rewards__panel--progress">
              <div className="rewards__panel-header rewards__panel-header--between">
                <h2 className="rewards__panel-title">Your Progression</h2>
                <span className="rewards__level-badge">
                  {isStatsLoading ? "Level …" : `Level ${xpLevel}`}
                </span>
              </div>

              <div className="rewards__progress-block">
                <div className="rewards__progress-info">
                  <span>
                    {isStatsLoading ? "Loading progress…" : progressLabel}
                  </span>
                  <span className="rewards__progress-percent">
                    {isStatsLoading ? "—" : `${progressPercent}%`}
                  </span>
                </div>
                <div
                  className="rewards__progress-bar"
                  role="progressbar"
                  aria-valuenow={progressPercent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label="Level progress"
                >
                  <div
                    className="rewards__progress-fill"
                    style={{
                      width: `${isStatsLoading ? 0 : progressPercent}%`,
                    }}
                  />
                </div>
                <p className="rewards__progress-fraction">
                  {isStatsLoading
                    ? "—"
                    : `${xpValue.toLocaleString()}xp / ${xpMax.toLocaleString()}xp`}
                </p>
              </div>

              <div className="rewards__progress-stats">
                <div className="rewards__progress-stat">
                  <span className="rewards__progress-stat-label">Total XP</span>
                  <span className="rewards__progress-stat-value">
                    {isStatsLoading
                      ? "—"
                      : dashboardStats.totalXp.toLocaleString()}
                  </span>
                </div>
                <div className="rewards__progress-stat">
                  <span className="rewards__progress-stat-label">
                    Current Level
                  </span>
                  <span className="rewards__progress-stat-value">
                    {isStatsLoading ? "—" : levelOrdinal}
                  </span>
                </div>
              </div>

              <div className="rewards__next-reward">
                <span>Next Level Treasure</span>
                <span className="rewards__next-reward-value">+25 Invites</span>
              </div>
            </div>

            <div className="rewards__panel rewards__panel--rewards">
              <div className="rewards__panel-header rewards__panel-header--between">
                <h2 className="rewards__panel-title">Available Treasure</h2>
                <span className="rewards__rewards-count">
                  {isRewardsLoading
                    ? "…"
                    : availableRewards.filter(reward => !reward.claimed).length}
                </span>
              </div>

              <div className="rewards__rewards-list">
                {isRewardsLoading
                  ? Array.from({ length: REWARD_SKELETON_COUNT }).map(
                      (_, index) => (
                        <div
                          key={`reward-skel-${index}`}
                          className="rewards__reward-item"
                          aria-hidden="true"
                        >
                          <div className="rewards__reward-main">
                            <span className="rewards__reward-icon">
                              <span className="amok-skeleton" />
                            </span>
                            <div className="rewards__reward-info">
                              <span className="amok-skeleton rewards__skel-line" />
                              <span className="amok-skeleton rewards__skel-line rewards__skel-line--sm" />
                            </div>
                          </div>
                        </div>
                      ),
                    )
                  : null}

                {!isRewardsLoading
                  ? availableRewards.map(reward => {
                      const isClaimed = Boolean(reward.claimed)
                      const isClaiming = claimingId === reward.claimId
                      const canClaim =
                        !isClaimed && !hasClaimedAtCurrentLevel && !claimingId
                      const showAsClaimed = isClaimed
                      return (
                        <div
                          key={reward.id}
                          className={`rewards__reward-item${
                            showAsClaimed
                              ? " rewards__reward-item--claimed"
                              : ""
                          }`}
                        >
                          <div className="rewards__reward-main">
                            <span
                              className="rewards__reward-icon"
                              aria-hidden="true"
                            >
                              <img
                                src={
                                  showAsClaimed
                                    ? ICONS.navRewards
                                    : reward.image || ICONS.navRewards
                                }
                                alt=""
                                width={16}
                                height={16}
                              />
                            </span>
                            <div className="rewards__reward-info">
                              <div className="rewards__reward-title">
                                {reward.title}
                              </div>
                              {reward.description ? (
                                <div className="rewards__reward-desc">
                                  {reward.description}
                                </div>
                              ) : null}
                            </div>
                          </div>
                          {showAsClaimed ? (
                            <CustomButton
                              variant="secondary"
                              size="sm"
                              type="button"
                              disabled
                              className="rewards__claimed-btn"
                              aria-disabled="true"
                            >
                              Claimed
                            </CustomButton>
                          ) : (
                            <CustomButton
                              variant="primary"
                              size="sm"
                              type="button"
                              className="rewards__claim-btn"
                              disabled={!canClaim}
                              onClick={() => handleClaimReward(reward)}
                            >
                              {isClaiming ? "Claiming…" : "Claim"}
                            </CustomButton>
                          )}
                        </div>
                      )
                    })
                  : null}

                {!isRewardsLoading && availableRewards.length === 0 ? (
                  <EmptyState
                    size="sm"
                    title="No rewards available"
                    description="Keep earning XP to unlock rewards."
                  />
                ) : null}
              </div>
            </div>
          </aside>
        </div>
      </div>

      <AddCollaboratorModal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        showProjectSelect
        title="Invite Collaborator"
        subtitle="Select a project and invite collaborators to join."
        onInvited={() => fetchDashboardStats({ silent: true })}
      />

      <XpLevelGuideModal
        isOpen={isGuideOpen}
        onClose={() => setIsGuideOpen(false)}
        level={xpLevel}
        xpValue={xpValue}
        xpMax={xpMax}
        progressPercent={progressPercent}
        xpToNextLabel={
          isStatsLoading
            ? ""
            : `${Math.max(0, xpMax - xpValue).toLocaleString()} XP to Next Level`
        }
      />
    </div>
  )
}

export default RewardsXp
